import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import {
  Calendar, Clock, PaperPlaneTilt, EnvelopeSimple, BellRinging,
  CheckCircle, Warning, Globe,
} from "@phosphor-icons/react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);

const EMPTY = {
  daily_enabled: false,
  weekly_enabled: false,
  daily_time_est: "09:00",
  weekly_time_est: "09:00",
  weekly_day: "Monday",
  email_recipients: "",
  alert_channel_ids: [],
};

export default function Digest() {
  const [cfg, setCfg] = useState(EMPTY);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState({});
  const [lastResult, setLastResult] = useState(null);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/digest/config"),
        api.get("/alert-channels"),
      ]);
      setCfg({
        ...EMPTY, ...a.data,
        email_recipients: (a.data.email_recipients || []).join(", "),
        alert_channel_ids: a.data.alert_channel_ids || [],
      });
      setChannels(b.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        daily_enabled: !!cfg.daily_enabled,
        weekly_enabled: !!cfg.weekly_enabled,
        daily_time_est: cfg.daily_time_est || "09:00",
        weekly_time_est: cfg.weekly_time_est || "09:00",
        weekly_day: cfg.weekly_day || "Monday",
        email_recipients: String(cfg.email_recipients || "")
          .split(",").map(s => s.trim()).filter(Boolean),
        alert_channel_ids: cfg.alert_channel_ids,
      };
      await api.put("/digest/config", payload);
      setToast("Saved");
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const sendNow = async (cadence) => {
    setSending({ ...sending, [cadence]: true });
    try {
      const { data } = await api.post(`/digest/send-now?cadence=${cadence}`);
      setLastResult({ cadence, ...data });
      setToast(`${cadence} digest sent — ${data.email_status} · ${data.teams_status}`);
      setTimeout(() => setToast(""), 6000);
    } catch (e) {
      setToast(`Send failed: ${formatApiError(e.response?.data?.detail) || e.message}`);
      setTimeout(() => setToast(""), 5000);
    } finally {
      setSending({ ...sending, [cadence]: false });
    }
  };

  const toggleChannel = (id) => {
    const has = cfg.alert_channel_ids.includes(id);
    setCfg({
      ...cfg,
      alert_channel_ids: has
        ? cfg.alert_channel_ids.filter(x => x !== id)
        : [...cfg.alert_channel_ids, id],
    });
  };

  const teamsChannels = channels.filter(
    c => c.type === "teams_graph" || c.type === "power_automate" || c.type === "teams" || c.type === "slack"
  );

  return (
    <Layout>
      <PageHeader
        title="Scheduled digest"
        subtitle="Daily + Weekly SLA digest · ALWAYS Teams + Email · all times in EST (DST-aware)"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => sendNow("daily")} disabled={sending.daily}
              data-testid="digest-send-daily-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded-md transition-colors">
              <PaperPlaneTilt size={14} weight="fill" /> {sending.daily ? "…" : "Send daily now"}
            </button>
            <button onClick={() => sendNow("weekly")} disabled={sending.weekly}
              data-testid="digest-send-weekly-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded-md transition-colors">
              <PaperPlaneTilt size={14} weight="fill" /> {sending.weekly ? "…" : "Send weekly now"}
            </button>
          </div>
        }
      />
      <div className="p-6 max-w-4xl space-y-6" data-testid="digest-page">
        {toast && (
          <div className="text-xs px-3 py-2 border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan rounded"
            data-testid="digest-toast">{toast}</div>
        )}
        {error && (
          <div className="text-xs px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-300 rounded">{error}</div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : (
          <form onSubmit={save} className="space-y-6">
            {/* Cadence block */}
            <section className="sw-card p-5 space-y-5" data-testid="digest-cadence-section">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">
                <Calendar size={14} weight="duotone" /> CADENCE — all times in EST (America/New_York)
              </div>

              {/* Daily */}
              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-border">
                <label className="flex items-center gap-2 text-sm text-slate-200 min-w-[200px]">
                  <input type="checkbox" checked={cfg.daily_enabled}
                    onChange={(e) => setCfg({ ...cfg, daily_enabled: e.target.checked })}
                    data-testid="digest-daily-enabled" />
                  <strong>Daily digest</strong> <span className="text-slate-400 text-xs">· 24-hour window</span>
                </label>
                <div className="flex items-center gap-2">
                  <Clock size={12} className="text-slate-400" />
                  <select value={cfg.daily_time_est}
                    onChange={(e) => setCfg({ ...cfg, daily_time_est: e.target.value })}
                    data-testid="digest-daily-time"
                    disabled={!cfg.daily_enabled}
                    className="bg-background-deep border border-border rounded px-2.5 py-1 text-xs text-slate-200 disabled:opacity-50">
                    {HOURS.map(h => <option key={h} value={h}>{h} EST</option>)}
                  </select>
                </div>
              </div>

              {/* Weekly */}
              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-border">
                <label className="flex items-center gap-2 text-sm text-slate-200 min-w-[200px]">
                  <input type="checkbox" checked={cfg.weekly_enabled}
                    onChange={(e) => setCfg({ ...cfg, weekly_enabled: e.target.checked })}
                    data-testid="digest-weekly-enabled" />
                  <strong>Weekly digest</strong> <span className="text-slate-400 text-xs">· 7-day window</span>
                </label>
                <div className="flex items-center gap-2">
                  <select value={cfg.weekly_day}
                    onChange={(e) => setCfg({ ...cfg, weekly_day: e.target.value })}
                    data-testid="digest-weekly-day"
                    disabled={!cfg.weekly_enabled}
                    className="bg-background-deep border border-border rounded px-2.5 py-1 text-xs text-slate-200 disabled:opacity-50">
                    {WEEKDAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <Clock size={12} className="text-slate-400" />
                  <select value={cfg.weekly_time_est}
                    onChange={(e) => setCfg({ ...cfg, weekly_time_est: e.target.value })}
                    data-testid="digest-weekly-time"
                    disabled={!cfg.weekly_enabled}
                    className="bg-background-deep border border-border rounded px-2.5 py-1 text-xs text-slate-200 disabled:opacity-50">
                    {HOURS.map(h => <option key={h} value={h}>{h} EST</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Recipients */}
            <section className="sw-card p-5 space-y-3" data-testid="digest-recipients-section">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">
                <EnvelopeSimple size={14} weight="duotone" /> EMAIL RECIPIENTS
              </div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
                Comma-separated emails
              </label>
              <input value={cfg.email_recipients}
                placeholder="ops@msp.com, leadership@msp.com"
                onChange={(e) => setCfg({ ...cfg, email_recipients: e.target.value })}
                data-testid="digest-email-recipients-input"
                className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors" />
              <p className="text-[11px] text-slate-500">
                Sent via the SMTP server configured under <code>Integrations</code>.
              </p>
            </section>

            {/* Teams / Slack channels */}
            <section className="sw-card p-5 space-y-3" data-testid="digest-channels-section">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">
                <BellRinging size={14} weight="duotone" /> TEAMS / SLACK CHANNELS
              </div>
              <p className="text-xs text-slate-400">
                Pick the channels that should receive the digest message. Per-client breakdown is included in the Teams version (longer message).
              </p>
              {teamsChannels.length === 0 ? (
                <div className="text-xs text-slate-500 border border-dashed border-border rounded p-3">
                  No Teams/Slack channels configured yet. Add one in <code>Alert channels</code>.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="digest-channels-list">
                  {teamsChannels.map(ch => {
                    const selected = cfg.alert_channel_ids.includes(ch.id);
                    return (
                      <label key={ch.id}
                        data-testid={`digest-channel-${ch.id}`}
                        className={`flex items-start gap-3 px-3 py-2 border rounded cursor-pointer transition-colors ${
                          selected ? "border-brand-cyan/50 bg-brand-cyan/10" : "border-border hover:border-brand-cyan/30 hover:bg-white/5"
                        }`}>
                        <input type="checkbox" checked={selected}
                          onChange={() => toggleChannel(ch.id)}
                          className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{ch.name}</div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                            {ch.type.replace("_", " ")}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="flex items-center justify-between pt-3">
              <div className="text-[11px] text-slate-500 flex items-center gap-2">
                <Globe size={12} weight="bold" />
                Scheduler runs hourly and dispatches at the configured EST hour (DST-aware).
              </div>
              <button type="submit" disabled={saving}
                data-testid="digest-save-btn"
                className="px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors disabled:opacity-50">
                {saving ? "Saving…" : "Save digest settings"}
              </button>
            </div>
          </form>
        )}

        {lastResult && (
          <section className="sw-card p-5" data-testid="digest-last-result">
            <h3 className="font-display text-sm font-bold tracking-tight mb-3">Last manual send — {lastResult.cadence}</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <ResultRow label="Window"   value={lastResult.window} />
              <ResultRow label="Clients"  value={lastResult.client_count} />
              <ResultRow label="Email"    value={lastResult.email_status}
                ok={lastResult.email_status?.startsWith("sent")} />
              <ResultRow label="Teams"    value={lastResult.teams_status}
                ok={lastResult.teams_status?.startsWith("sent")} />
              <ResultRow label="Open"     value={lastResult.overall?.incidents_open ?? 0} />
              <ResultRow label="Closed"   value={lastResult.overall?.incidents_closed ?? 0} />
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}

function ResultRow({ label, value, ok }) {
  const colorClass = ok === undefined
    ? "text-slate-200"
    : ok ? "text-emerald-300" : "text-red-300";
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 uppercase tracking-[0.15em] text-[10px]">{label}</span>
      {ok === true && <CheckCircle size={11} className="text-emerald-400" weight="fill" />}
      {ok === false && <Warning size={11} className="text-red-400" weight="fill" />}
      <span className={`flex-1 truncate ${colorClass}`}>{String(value)}</span>
    </div>
  );
}
